import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Pro Pass 24h countdown chip.
 * - When the user holds a non-expired `pro_pass_until` timestamp, this chip replaces the
 *   default "Upgrade"/plan chip and pulses with the remaining time (Xh Ym).
 * - In the final 60 minutes it switches to live "MM:SS" precision and an urgent red-violet
 *   glow to drive the urgency the user paid 300 gems for.
 * - Hidden if no pro_pass_until, or if it's already expired.
 *
 * Props:
 *   - until: ISO string from user.pro_pass_until
 *   - "data-testid": test hook
 */
export default function ProPassChip({ until, "data-testid": testId = "pro-pass-chip" }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!until) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [until]);

  if (!until) return null;

  let remainingMs;
  try {
    remainingMs = new Date(until).getTime() - now;
  } catch {
    return null;
  }
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return null;

  const totalSeconds = Math.floor(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const urgent = remainingMs <= 60 * 60 * 1000; // last hour

  let label;
  if (urgent) {
    label = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  } else if (hours > 0) {
    label = `${hours}h ${String(minutes).padStart(2, "0")}m`;
  } else {
    label = `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  }

  const palette = urgent
    ? {
        bg: "linear-gradient(135deg, rgba(252,165,165,0.30), rgba(124,92,255,0.25))",
        border: "rgba(252,165,165,0.55)",
        glow: "0 0 18px -2px rgba(252,165,165,0.65)",
        text: "#FECACA",
        icon: "#FCA5A5",
      }
    : {
        bg: "linear-gradient(135deg, rgba(124,92,255,0.30), rgba(90,200,250,0.20))",
        border: "rgba(124,92,255,0.50)",
        glow: "0 0 18px -2px rgba(124,92,255,0.60)",
        text: "#FFFFFF",
        icon: "#B8A4FF",
      };

  return (
    <Link
      to="/app/billing"
      data-testid={testId}
      title={`Pro Pass active — ${label} remaining. Tap to extend.`}
      className="relative hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.14em]"
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.text,
        boxShadow: palette.glow,
      }}
    >
      {/* Pulsing halo */}
      <motion.span
        aria-hidden
        className="absolute inset-0 rounded-full pointer-events-none"
        animate={{
          boxShadow: urgent
            ? ["0 0 0px rgba(252,165,165,0)", "0 0 22px rgba(252,165,165,0.85)", "0 0 0px rgba(252,165,165,0)"]
            : ["0 0 0px rgba(124,92,255,0)", "0 0 18px rgba(124,92,255,0.7)", "0 0 0px rgba(124,92,255,0)"],
        }}
        transition={{ duration: urgent ? 1.2 : 2.2, repeat: Infinity, ease: "easeInOut" }}
      />
      <Zap size={11} style={{ color: palette.icon }} fill={palette.icon} strokeWidth={2.2} />
      <AnimatePresence mode="popLayout">
        <motion.span
          key={label}
          initial={{ opacity: 0, y: -3 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 3 }}
          transition={{ duration: 0.15 }}
          className="tabular-nums relative"
        >
          {label}
        </motion.span>
      </AnimatePresence>
    </Link>
  );
}
